export const DEPLOYMENT_PIPELINE_STAGE_IDS = [
  "queued",
  "clone",
  "detect",
  "install",
  "build",
  "artifacts",
  "runtime",
  "preview",
  "live"
] as const;

export type DeploymentPipelineStageId = (typeof DEPLOYMENT_PIPELINE_STAGE_IDS)[number];

export type DeploymentPipelineStageStatus =
  | "pending"
  | "running"
  | "success"
  | "failed"
  | "skipped"
  | "cancelled";

export type DeploymentPipelineStageEvent = {
  stage: DeploymentPipelineStageId;
  status: Exclude<DeploymentPipelineStageStatus, "pending">;
  detail?: string;
};

const STAGE_LOG_MARKER_PREFIX = "[deployher:stage]";

export const formatDeploymentStageLogMarker = (
  stage: DeploymentPipelineStageId,
  status: Exclude<DeploymentPipelineStageStatus, "pending">,
  detail?: string
): string =>
  `${STAGE_LOG_MARKER_PREFIX} ${stage} ${status}${detail ? ` — ${detail}` : ""}`;

export const extractDeploymentStageLog = (
  logText: string,
  stage: DeploymentPipelineStageId
): string | null => {
  const marker = `${STAGE_LOG_MARKER_PREFIX} `;
  let activeStage: DeploymentPipelineStageId | null = null;
  const selected: string[] = [];

  for (const line of logText.split("\n")) {
    if (line.startsWith(marker)) {
      const candidate = line.slice(marker.length).split(" ", 1)[0] ?? "";
      activeStage = isDeploymentPipelineStageId(candidate) ? candidate : null;
    }
    if (activeStage === stage) selected.push(line);
  }

  const output = selected.join("\n").trim();
  return output || null;
};

export type DeploymentPipelineNode = {
  id: DeploymentPipelineStageId;
  status: DeploymentPipelineStageStatus;
  detail?: string;
};

export const isDeploymentPipelineStageId = (
  value: string
): value is DeploymentPipelineStageId =>
  (DEPLOYMENT_PIPELINE_STAGE_IDS as readonly string[]).includes(value);

const fallbackNodes = (deploymentStatus: string): DeploymentPipelineNode[] => {
  const status = deploymentStatus.toLowerCase();
  const terminalStatus: DeploymentPipelineStageStatus =
    status === "success" ? "success" : status === "failed" ? "failed" : "pending";

  return DEPLOYMENT_PIPELINE_STAGE_IDS.map((id, index) => {
    if (status === "success") return { id, status: "success" };
    if (status === "failed") {
      // Without stage history, only mark the early stages complete and fail install —
      // do not invent clone/detect/install progress for live builds.
      if (index < 3) return { id, status: "success" };
      if (index === 3) return { id, status: terminalStatus };
      return { id, status: "pending" };
    }
    if (status === "queued") {
      return { id, status: id === "queued" ? "running" : "pending" };
    }
    if (status === "building") {
      // Honest fallback: we know a worker started, not which stage is active.
      return {
        id,
        status: id === "queued" ? "success" : "pending",
        ...(id === "queued" ? { detail: "Build worker started" } : {})
      };
    }
    return { id, status: "pending" };
  });
};

export const buildDeploymentPipelineNodes = (
  deploymentStatus: string,
  events: readonly DeploymentPipelineStageEvent[]
): DeploymentPipelineNode[] => {
  if (events.length === 0) return fallbackNodes(deploymentStatus);

  const latest = new Map<DeploymentPipelineStageId, DeploymentPipelineStageEvent>();
  for (const event of events) latest.set(event.stage, event);

  const nodes = DEPLOYMENT_PIPELINE_STAGE_IDS.map((id) => {
    const event = latest.get(id);
    const inferredQueuedStatus =
      id === "queued" && !event
        ? deploymentStatus === "queued"
          ? "running"
          : "success"
        : undefined;
    return {
      id,
      status: event?.status ?? inferredQueuedStatus ?? "pending",
      ...(event?.detail ? { detail: event.detail } : {})
    } satisfies DeploymentPipelineNode;
  });

  if (deploymentStatus === "failed" && !nodes.some((node) => node.status === "failed")) {
    const running = [...nodes].reverse().find((node) => node.status === "running");
    if (running) {
      running.status = "failed";
    } else {
      const firstPending = nodes.find((node) => node.status === "pending");
      if (firstPending) firstPending.status = "failed";
    }
  }

  return nodes;
};
